/**
 * One page of an Intake hub queue, plus the true total behind it.
 *
 * Only for a query that returns one record per row: the total is a count of the
 * root records, so an Appointment-rooted query that collapses to one row per
 * patient would show the wrong N. Those tabs keep loading their whole cohort
 * until their scope is rebuilt.
 */

import { Resource } from '@medplum/fhirtypes';
import { useMemo, useState } from 'react';
import { useLiveGraphQL } from '@/hooks/useLiveGraphQL';
import { useCohortTotal } from './useCohortTotal';

export const QUEUE_PAGE_ROWS = 100;

export function useQueuePage<T extends Resource>(
  query: string,
  resourceType: 'Patient' | 'Appointment',
  filter: string,
  subscriptionParams: Record<string, string | string[]>,
  /** The query's `$sort`, for a query that takes one. */
  sort?: string
) {
  const [page, setPage] = useState(1);
  const [prevFilter, setPrevFilter] = useState(filter);

  // Any filter change returns to page one.
  if (prevFilter !== filter) {
    setPrevFilter(filter);
    setPage(1);
  }

  const variables = useMemo(() => (sort ? { filter, sort } : { filter }), [filter, sort]);
  const { data, loading, error } = useLiveGraphQL<T>(query, variables, {
    enablePagination: true,
    pageSize: QUEUE_PAGE_ROWS,
    offset: (page - 1) * QUEUE_PAGE_ROWS,
    subscriptionParams,
  });
  const total = useCohortTotal(resourceType, filter);

  return { rows: data, total, loading, error, page, setPage };
}
